/**
 * Create missive page.
 * Form for composing a new missive with recipient search and optional resource attachment.
 *
 * Features a fantasy parchment aesthetic with scroll-like borders
 * and warm paper-like colors for an immersive experience.
 */

import { Head, Link, router } from '@inertiajs/react';
import { useState, useMemo } from 'react';
import { Editor } from '@/components/Editor/Editor';

/** Resource amounts - index signature required for Inertia form data */
interface Resources {
    [key: string]: number;
    tea: number;
    wood: number;
    mystical: number;
    medicinal: number;
    edible: number;
}

/** Pre-filled recipient from URL */
interface RecipientData {
    id: number;
    username: string;
}

/** Portal capabilities from backend */
interface PortalCapabilities {
    hasPortal: boolean;
    level: number;
    stars: number;
    canSendResources: boolean;
    canSendBlueprints: boolean;
    canSendItems: boolean;
    resourceCostPer50: number;
    itemCost: number;
}

/** Item data from backend */
interface ItemData {
    id: number;
    name: string;
    rarity: string;
    quality?: number;
}

interface Props {
    recipient: RecipientData | null;
    userResources: Resources;
    portal: PortalCapabilities;
    userItems: ItemData[];
    userBlueprints: ItemData[];
}

/** Resource type configuration */
const RESOURCE_TYPES: { key: keyof Resources; label: string; icon: string }[] = [
    { key: 'tea', label: 'Tea', icon: '🍵' },
    { key: 'wood', label: 'Wood', icon: '🪵' },
    { key: 'mystical', label: 'Mystical', icon: '✨' },
    { key: 'medicinal', label: 'Medicinal', icon: '🌿' },
    { key: 'edible', label: 'Edible', icon: '🍎' },
];

/**
 * Decorative corner flourish SVG component for parchment borders.
 */
function CornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-10 h-10 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

/**
 * Wax seal component for visual flair.
 */
function WaxSeal() {
    return (
        <div
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-red-700 to-red-900"
            style={{
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="text-sm font-serif text-amber-100/80">✦</span>
        </div>
    );
}

/** Rarity color mapping for items */
const RARITY_COLORS: Record<string, string> = {
    common: 'text-slate-600 bg-slate-100 border-slate-300',
    uncommon: 'text-green-700 bg-green-100 border-green-300',
    rare: 'text-blue-700 bg-blue-100 border-blue-300',
    epic: 'text-purple-700 bg-purple-100 border-purple-300',
    legendary: 'text-amber-700 bg-amber-100 border-amber-300',
};

/**
 * Create Missive page - compose and send a new missive.
 */
export default function Create({ recipient, userResources, portal, userItems, userBlueprints }: Props) {
    const [recipientUsername, setRecipientUsername] = useState(recipient?.username || '');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [resources, setResources] = useState<Resources>({
        tea: 0, wood: 0, mystical: 0, medicinal: 0, edible: 0,
    });
    const [showResources, setShowResources] = useState(false);
    const [showBlueprints, setShowBlueprints] = useState(false);
    const [showItems, setShowItems] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /** Update a single resource amount */
    const updateResource = (key: keyof Resources, value: number) => {
        const maxValue = userResources[key];
        const clampedValue = Math.max(0, Math.min(value, maxValue));
        setResources((prev) => ({ ...prev, [key]: clampedValue }));
    };

    /** Toggle item selection */
    const toggleItem = (id: number) => {
        setSelectedItemIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    /** Check if any resources are attached */
    const hasResources = Object.values(resources).some((v) => v > 0);

    /** Check if any items are attached */
    const hasItems = selectedItemIds.length > 0;

    /** Calculate total resources for cost */
    const totalResources = useMemo(() => {
        return Object.values(resources).reduce((sum, val) => sum + val, 0);
    }, [resources]);

    /** Calculate Mystical cost */
    const mysticalCost = useMemo(() => {
        if (!portal.hasPortal) return 0;
        let cost = 0;
        if (totalResources > 0) {
            const resourceUnits = Math.ceil(totalResources / 50);
            cost += resourceUnits * portal.resourceCostPer50;
        }
        cost += selectedItemIds.length * portal.itemCost;
        return cost;
    }, [portal, totalResources, selectedItemIds]);

    /** Check if user has enough Mystical */
    const hasEnoughMystical = userResources.mystical >= mysticalCost;

    /** Check if attachments require Portal but user doesn't have one */
    const needsPortalForAttachments = (hasResources || hasItems) && !portal.hasPortal;

    /** Handle form submission */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Client-side validation
        const newErrors: Record<string, string> = {};
        if (!recipientUsername.trim()) {
            newErrors.recipient_username = 'Recipient is required';
        }
        if (!subject.trim()) {
            newErrors.subject = 'Subject is required';
        }
        if (!content.trim()) {
            newErrors.content = 'Message content is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        router.post('/missives', {
            recipient_username: recipientUsername.trim(),
            subject: subject.trim(),
            content,
            resources: hasResources ? resources : undefined,
            item_ids: hasItems ? selectedItemIds : undefined,
        }, {
            onError: (errors) => {
                setErrors(errors as Record<string, string>);
                setIsSubmitting(false);
            },
            onFinish: () => {
                setIsSubmitting(false);
            },
        });
    };

    return (
        <>
            <Head title="New Missive" />

            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 py-8">
                    {/* Header - kept in dark theme */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link
                            href="/missives"
                            className="text-slate-300 hover:text-white transition-colors"
                        >
                            ← Back to Missives
                        </Link>
                    </div>

                    {/* Parchment scroll container */}
                    <div className="relative">
                        {/* Parchment background */}
                        <div
                            className="relative rounded-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                            }}
                        >
                            {/* Decorative corner flourishes */}
                            <CornerFlourish className="absolute top-2 left-2" />
                            <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                            <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                            <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                            {/* Aged paper edge effects */}
                            <div
                                className="absolute top-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)' }}
                            />
                            <div
                                className="absolute bottom-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)' }}
                            />

                            {/* Inner content area */}
                            <div className="relative p-6 pt-8">
                                {/* Ornate header */}
                                <div className="text-center mb-6">
                                    <div className="inline-flex items-center gap-3 mb-2">
                                        <WaxSeal />
                                        <h1 className="text-2xl font-serif text-amber-900 tracking-wide">
                                            ✒️ Compose New Missive
                                        </h1>
                                        <WaxSeal />
                                    </div>
                                    <p className="text-sm font-serif italic text-amber-800/60">
                                        Pen your correspondence with care
                                    </p>
                                </div>

                                {/* Decorative divider */}
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                    <span className="text-amber-800/40 text-lg">✦</span>
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                                </div>

                                {/* Portal Status Banner */}
                                {portal.hasPortal ? (
                                    <div className="mb-6 p-3 bg-purple-100/50 border border-purple-300/50 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">🌀</span>
                                                <span className="font-serif text-purple-900 font-medium">
                                                    Portal Level {portal.level}
                                                </span>
                                                <span className="text-amber-500">
                                                    {'★'.repeat(portal.stars)}{'☆'.repeat(5 - portal.stars)}
                                                </span>
                                            </div>
                                            <div className="text-xs font-serif text-purple-800/70 flex gap-3">
                                                <span className={portal.canSendResources ? 'text-green-700' : 'text-red-700'}>
                                                    {portal.canSendResources ? '✓' : '✗'} Resources
                                                </span>
                                                <span className={portal.canSendBlueprints ? 'text-green-700' : 'text-red-700'}>
                                                    {portal.canSendBlueprints ? '✓' : '✗'} Blueprints
                                                </span>
                                                <span className={portal.canSendItems ? 'text-green-700' : 'text-red-700'}>
                                                    {portal.canSendItems ? '✓' : '✗'} Items
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-6 p-4 bg-amber-100/70 border border-amber-400/50 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <span className="text-xl">⚠️</span>
                                            <div>
                                                <p className="font-serif text-amber-900 font-medium">
                                                    Portal Required for Attachments
                                                </p>
                                                <p className="text-sm text-amber-800/70 mt-1 font-serif">
                                                    Build a Portal in your hex to send resources, blueprints, and items.
                                                </p>
                                                <ul className="text-xs text-amber-700/60 mt-2 font-serif space-y-0.5">
                                                    <li>• Level 1+: Send Resources</li>
                                                    <li>• Level 3+: Send Blueprints</li>
                                                    <li>• Level 5: Send Items</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {/* Recipient */}
                                    <div>
                                        <label htmlFor="recipient" className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                            📬 Recipient
                                        </label>
                                        <input
                                            type="text"
                                            id="recipient"
                                            value={recipientUsername}
                                            onChange={(e) => setRecipientUsername(e.target.value)}
                                            className="w-full px-4 py-3 bg-white/50 border border-amber-400/50 rounded-lg text-amber-900 placeholder-amber-700/40 font-serif focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                            placeholder="Enter the recipient's username..."
                                        />
                                        {errors.recipient_username && (
                                            <p className="mt-2 text-sm text-red-700 font-serif">{errors.recipient_username}</p>
                                        )}
                                    </div>

                                    {/* Subject */}
                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                            📜 Subject
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            className="w-full px-4 py-3 bg-white/50 border border-amber-400/50 rounded-lg text-amber-900 placeholder-amber-700/40 font-serif focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                            placeholder="Enter the subject of your letter..."
                                            maxLength={200}
                                        />
                                        {errors.subject && (
                                            <p className="mt-2 text-sm text-red-700 font-serif">{errors.subject}</p>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <label className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                            ✍️ Your Message
                                        </label>
                                        <div className="bg-white/50 border border-amber-400/50 rounded-lg overflow-hidden">
                                            <Editor
                                                value={content}
                                                onChange={(value) => setContent(value)}
                                                placeholder="Write your message here..."
                                                height={250}
                                            />
                                        </div>
                                        {errors.content && (
                                            <p className="mt-2 text-sm text-red-700 font-serif">{errors.content}</p>
                                        )}
                                    </div>

                                    {/* Attachments Section */}
                                    <div className="border-t border-amber-800/20 pt-5 space-y-4">
                                        {/* Resource Attachment Toggle */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => portal.canSendResources && setShowResources(!showResources)}
                                                disabled={!portal.canSendResources}
                                                className={`flex items-center gap-2 font-serif ${
                                                    portal.canSendResources
                                                        ? 'text-amber-800 hover:text-amber-900 cursor-pointer'
                                                        : 'text-amber-600/50 cursor-not-allowed'
                                                } transition-colors`}
                                            >
                                                <span className="text-lg">💰</span>
                                                <span>Attach Resources</span>
                                                {portal.hasPortal && (
                                                    <span className="text-xs text-purple-700/70">
                                                        ({portal.resourceCostPer50} ✨ per 50)
                                                    </span>
                                                )}
                                                {portal.canSendResources ? (
                                                    <span className="text-xs text-amber-700/60">
                                                        {showResources ? '▼' : '▶'}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-red-600">(Requires Portal L1)</span>
                                                )}
                                            </button>

                                            {/* Resource Inputs */}
                                            {showResources && portal.canSendResources && (
                                                <div className="mt-4 p-4 bg-amber-100/50 rounded-lg border border-amber-400/50">
                                                    <p className="text-sm text-amber-800/70 mb-4 font-serif italic">
                                                        Include resources as a gift. Costs Mystical to send via Portal.
                                                    </p>
                                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                                                        {RESOURCE_TYPES.map(({ key, label, icon }) => (
                                                            <div key={key}>
                                                                <label className="block text-xs text-amber-800 mb-1 font-serif">
                                                                    {icon} {label}
                                                                    <span className="text-amber-700/60 ml-1">
                                                                        (max: {userResources[key]})
                                                                    </span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    max={userResources[key]}
                                                                    value={resources[key]}
                                                                    onChange={(e) => updateResource(key, parseInt(e.target.value) || 0)}
                                                                    className="w-full px-3 py-2 bg-white/70 border border-amber-400/50 rounded text-amber-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {errors.resources && (
                                                        <p className="mt-2 text-sm text-red-700 font-serif">{errors.resources}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Blueprint Attachment Toggle */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => portal.canSendBlueprints && setShowBlueprints(!showBlueprints)}
                                                disabled={!portal.canSendBlueprints}
                                                className={`flex items-center gap-2 font-serif ${
                                                    portal.canSendBlueprints
                                                        ? 'text-amber-800 hover:text-amber-900 cursor-pointer'
                                                        : 'text-amber-600/50 cursor-not-allowed'
                                                } transition-colors`}
                                            >
                                                <span className="text-lg">📜</span>
                                                <span>Attach Blueprints</span>
                                                {portal.hasPortal && (
                                                    <span className="text-xs text-purple-700/70">
                                                        ({portal.itemCost} ✨ each)
                                                    </span>
                                                )}
                                                {portal.canSendBlueprints ? (
                                                    <span className="text-xs text-amber-700/60">
                                                        {showBlueprints ? '▼' : '▶'}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-red-600">(Requires Portal L3)</span>
                                                )}
                                            </button>

                                            {/* Blueprint Selection */}
                                            {showBlueprints && portal.canSendBlueprints && (
                                                <div className="mt-4 p-4 bg-blue-50/50 rounded-lg border border-blue-300/50">
                                                    {userBlueprints.length === 0 ? (
                                                        <p className="text-sm text-blue-800/70 font-serif italic">
                                                            You have no blueprints to send.
                                                        </p>
                                                    ) : (
                                                        <>
                                                            <p className="text-sm text-blue-800/70 mb-3 font-serif italic">
                                                                Select blueprints to send. These will be transferred to the recipient.
                                                            </p>
                                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                                {userBlueprints.map((item) => (
                                                                    <label
                                                                        key={item.id}
                                                                        className="flex items-center gap-3 p-2 bg-white/50 rounded border border-blue-200/50 cursor-pointer hover:bg-white/70"
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedItemIds.includes(item.id)}
                                                                            onChange={() => toggleItem(item.id)}
                                                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                                        />
                                                                        <span className="font-serif text-amber-900">{item.name}</span>
                                                                        <span className={`text-xs px-2 py-0.5 rounded border ${RARITY_COLORS[item.rarity] || RARITY_COLORS.common}`}>
                                                                            {item.rarity}
                                                                        </span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                    {errors.blueprints && (
                                                        <p className="mt-2 text-sm text-red-700 font-serif">{errors.blueprints}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Item Attachment Toggle */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => portal.canSendItems && setShowItems(!showItems)}
                                                disabled={!portal.canSendItems}
                                                className={`flex items-center gap-2 font-serif ${
                                                    portal.canSendItems
                                                        ? 'text-amber-800 hover:text-amber-900 cursor-pointer'
                                                        : 'text-amber-600/50 cursor-not-allowed'
                                                } transition-colors`}
                                            >
                                                <span className="text-lg">🎒</span>
                                                <span>Attach Items</span>
                                                {portal.hasPortal && (
                                                    <span className="text-xs text-purple-700/70">
                                                        ({portal.itemCost} ✨ each)
                                                    </span>
                                                )}
                                                {portal.canSendItems ? (
                                                    <span className="text-xs text-amber-700/60">
                                                        {showItems ? '▼' : '▶'}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-red-600">(Requires Portal L5)</span>
                                                )}
                                            </button>

                                            {/* Item Selection */}
                                            {showItems && portal.canSendItems && (
                                                <div className="mt-4 p-4 bg-green-50/50 rounded-lg border border-green-300/50">
                                                    {userItems.length === 0 ? (
                                                        <p className="text-sm text-green-800/70 font-serif italic">
                                                            You have no unequipped items to send.
                                                        </p>
                                                    ) : (
                                                        <>
                                                            <p className="text-sm text-green-800/70 mb-3 font-serif italic">
                                                                Select items to send. Only unequipped items can be sent.
                                                            </p>
                                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                                {userItems.map((item) => (
                                                                    <label
                                                                        key={item.id}
                                                                        className="flex items-center gap-3 p-2 bg-white/50 rounded border border-green-200/50 cursor-pointer hover:bg-white/70"
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedItemIds.includes(item.id)}
                                                                            onChange={() => toggleItem(item.id)}
                                                                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                                                        />
                                                                        <span className="font-serif text-amber-900">{item.name}</span>
                                                                        <span className={`text-xs px-2 py-0.5 rounded border ${RARITY_COLORS[item.rarity] || RARITY_COLORS.common}`}>
                                                                            {item.rarity}
                                                                        </span>
                                                                        {item.quality && item.quality > 0 && (
                                                                            <span className="text-xs text-amber-600">
                                                                                +{item.quality}
                                                                            </span>
                                                                        )}
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                    {errors.items && (
                                                        <p className="mt-2 text-sm text-red-700 font-serif">{errors.items}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Mystical Cost Summary */}
                                        {portal.hasPortal && (hasResources || hasItems) && (
                                            <div className={`p-3 rounded-lg border ${hasEnoughMystical ? 'bg-purple-50/50 border-purple-300/50' : 'bg-red-50/50 border-red-300/50'}`}>
                                                <div className="flex items-center justify-between">
                                                    <span className="font-serif text-sm text-purple-900">
                                                        ✨ Mystical Cost
                                                    </span>
                                                    <span className={`font-serif font-bold ${hasEnoughMystical ? 'text-purple-900' : 'text-red-700'}`}>
                                                        {mysticalCost}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs mt-1">
                                                    <span className="text-purple-700/70">Your Mystical</span>
                                                    <span className={hasEnoughMystical ? 'text-green-700' : 'text-red-700'}>
                                                        {userResources.mystical} {hasEnoughMystical ? '✓' : '(insufficient)'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {errors.portal && (
                                            <p className="text-sm text-red-700 font-serif">{errors.portal}</p>
                                        )}
                                        {errors.mystical && (
                                            <p className="text-sm text-red-700 font-serif">{errors.mystical}</p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-4 pt-4">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting || needsPortalForAttachments || !hasEnoughMystical}
                                            className="px-6 py-3 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-700 disabled:to-amber-800 disabled:cursor-not-allowed rounded-lg font-serif font-medium transition-all shadow-lg border border-amber-500/30 text-white flex items-center gap-2"
                                        >
                                            {isSubmitting ? (
                                                <>Dispatching...</>
                                            ) : (
                                                <>
                                                    📤 Dispatch Missive
                                                    {hasResources && <span className="text-amber-200">+ 💰</span>}
                                                    {hasItems && <span className="text-amber-200">+ 🎒</span>}
                                                </>
                                            )}
                                        </button>
                                        <Link
                                            href="/missives"
                                            className="px-6 py-3 bg-amber-200/50 hover:bg-amber-200 text-amber-800 rounded-lg font-serif font-medium transition-colors border border-amber-400/30"
                                        >
                                            Cancel
                                        </Link>
                                    </div>
                                </form>

                                {/* Bottom flourish */}
                                <div className="flex items-center justify-center gap-2 mt-8">
                                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                                    <span className="text-amber-800/30 text-sm">❧</span>
                                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                                </div>
                            </div>
                        </div>

                        {/* Shadow beneath the parchment */}
                        <div
                            className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                            style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

